import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { translate } from '@smovilgps/optimus-intl';
import { Form, Input, InputNumber, Radio, Select } from 'antd';
import { RootState } from '@/store/configureStore';
import { searchInString } from '@/utilities/util';
import { BILLING_INTERVAL, PAYMENT_METHOD } from './constants';

type ChannelOptionsType = {
	id: number;
	description: string;
};

type Props = {
	hasSubscription: boolean;
};

const AddOrEditForm = ({ hasSubscription }: Props) => {
	const intl = useIntl();
	const paymentMethod = Form.useWatch('type');
	const modelProfiles = useSelector((state: RootState) => state.modelProfile.profiles);
	const modelProfileOptions = modelProfiles.map(({ id, description }: ChannelOptionsType) => ({
		key: id,
		value: id,
		label: description,
	}));

	return (
		<>
			<Form.Item
				name="modelProfileId"
				label={intl.formatMessage(translate('model'))}
				rules={[
					{
						required: true,
						message: intl.formatMessage(translate('deviceModelError')),
					},
				]}
			>
				<Select
					showSearch
					disabled={hasSubscription}
					placeholder={intl.formatMessage(translate('models'))}
					optionFilterProp="children"
					filterOption={(input, option) => searchInString(input, option?.label)}
					options={modelProfileOptions}
				/>
			</Form.Item>
			<Form.Item
				name="description"
				label={intl.formatMessage(translate('description'))}
				rules={[
					{
						required: true,
						message: intl.formatMessage(translate('descriptionError')),
					},
				]}
			>
				<Input placeholder={intl.formatMessage(translate('description'))} />
			</Form.Item>
			<Form.Item
				name="amount"
				label={intl.formatMessage(translate('price'))}
				rules={[
					{
						required: true,
						message: intl.formatMessage(translate('priceError')),
					},
				]}
			>
				<InputNumber
					min={1}
					disabled={hasSubscription}
					placeholder={intl.formatMessage(translate('price'))}
					addonBefore="$"
					style={{ width: '100%' }}
					precision={2}
					parser={(value?: string) => {
						if (!value) return 0;
						const data = value.replace(/[^0-9.]/g, '');
						return data.length > 0 ? Number(data) : 0;
					}}
				/>
			</Form.Item>
			<Form.Item label={intl.formatMessage(translate('paymentMethod'))} name="type" rules={[{ required: true }]}>
				<Radio.Group disabled={hasSubscription} className="radio-group-full">
					<Radio.Button value={PAYMENT_METHOD.PUNTUAL.key} className="radio-button-full">
						<FormattedMessage {...translate('punctual')} />
					</Radio.Button>
					<Radio.Button value={PAYMENT_METHOD.RECURRING.key} className="radio-button-full">
						<FormattedMessage {...translate('recurrent')} />
					</Radio.Button>
				</Radio.Group>
			</Form.Item>
			{paymentMethod === PAYMENT_METHOD.RECURRING.key && (
				<Form.Item label={intl.formatMessage(translate('billingPeriod'))} required>
					<Input.Group compact>
						<Form.Item name="intervalCount" initialValue={1} noStyle rules={[{ required: true }]}>
							<InputNumber
								min={1}
								disabled={hasSubscription}
								addonBefore={intl.formatMessage(translate('every'))}
								style={{ width: '40%' }}
							/>
						</Form.Item>
						<Form.Item
							name="interval"
							noStyle
							initialValue={BILLING_INTERVAL.MONTHLY.key}
							rules={[{ required: true }]}
						>
							<Select disabled={hasSubscription} style={{ width: '60%' }}>
								<Select.Option value={BILLING_INTERVAL.DAILY.key}>
									{intl.formatMessage(translate('days'))}
								</Select.Option>
								<Select.Option value={BILLING_INTERVAL.MONTHLY.key}>
									{intl.formatMessage(translate('months'))}
								</Select.Option>
								<Select.Option value={BILLING_INTERVAL.ANNUAL.key}>
									{intl.formatMessage(translate('annual'))}
								</Select.Option>
							</Select>
						</Form.Item>
					</Input.Group>
				</Form.Item>
			)}
			<Form.Item
				label={intl.formatMessage(translate('status'))}
				name="status"
				initialValue={true}
				rules={[{ required: true }]}
			>
				<Radio.Group disabled={hasSubscription} className="radio-group-full">
					<Radio.Button value={false} className="radio-button-full">
						<FormattedMessage {...translate('inactive')} />
					</Radio.Button>
					<Radio.Button value={true} className="radio-button-full">
						<FormattedMessage {...translate('active')} />
					</Radio.Button>
				</Radio.Group>
			</Form.Item>
			<Form.Item
				label={intl.formatMessage(translate('mode'))}
				name="isTest"
				initialValue={false}
				rules={[
					{
						required: true,
					},
				]}
			>
				<Radio.Group disabled={hasSubscription} className="radio-group-full">
					<Radio.Button value={false} className="radio-button-full">
						<FormattedMessage {...translate('live')} />
					</Radio.Button>
					<Radio.Button value={true} className="radio-button-full">
						<FormattedMessage {...translate('test')} />
					</Radio.Button>
				</Radio.Group>
			</Form.Item>
		</>
	);
};

export default AddOrEditForm;
